import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeft, Send, Phone } from 'lucide-react';
import { CapacitorSafePoller, makePhoneCall, triggerHapticFeedback } from '../../utils/capacitorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * In-app ride chat between rider and driver.
 * Reusable on both sides — pass senderRole ('rider' | 'driver'), senderId and
 * the ride id. Uses the existing /api/chat backend (polled every 3s).
 */
const RideChat = ({ rideId, senderId, senderRole, recipientName = 'Driver', recipientPhone, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollerRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/chat/ride/${rideId}`);
      const data = await res.json();
      if (data?.success) setMessages(data.messages || []);
    } catch (e) {
      /* silent — will retry on next poll */
    }
  }, [rideId]);

  useEffect(() => {
    if (!rideId) return undefined;
    loadMessages();
    // mark the other party's messages as read
    fetch(`${API}/chat/mark-read/${rideId}?user_role=${senderRole}`, { method: 'POST' }).catch(() => {});
    pollerRef.current = new CapacitorSafePoller(loadMessages, 3000);
    pollerRef.current.start();
    return () => pollerRef.current?.stop();
  }, [rideId, senderRole, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    triggerHapticFeedback('light');
    // optimistic append
    const optimistic = {
      id: `tmp-${Date.now()}`,
      ride_id: rideId,
      sender_id: senderId,
      sender_role: senderRole,
      message: body,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setText('');
    try {
      await fetch(`${API}/chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ride_id: rideId,
          sender_id: senderId,
          sender_role: senderRole,
          message: body,
        }),
      });
      loadMessages();
    } catch (e) {
      /* keep optimistic message; next poll reconciles */
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85] flex flex-col bg-[#f2f5f7]" data-testid="ride-chat">
      {/* Header */}
      <header className="flex items-center gap-3 px-4 h-16 bg-emerald-600 text-white shadow-md flex-shrink-0">
        <button onClick={onClose} data-testid="chat-back" aria-label="Back" className="active:scale-90 transition-transform">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate leading-tight">{recipientName}</p>
          <p className="text-xs text-emerald-100">Ride chat</p>
        </div>
        {recipientPhone && (
          <button
            onClick={() => makePhoneCall(recipientPhone)}
            data-testid="chat-call"
            aria-label="Call"
            className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center active:scale-90 transition-transform"
          >
            <Phone className="w-5 h-5" />
          </button>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="chat-messages">
        {messages.length === 0 && (
          <p className="text-center text-sm text-gray-400 mt-8">
            Say hello 👋 — messages are shared with your {senderRole === 'rider' ? 'driver' : 'rider'}.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.sender_role === senderRole;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-snug shadow-sm ${
                  mine
                    ? 'bg-emerald-600 text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md'
                }`}
              >
                {m.message}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="flex items-center gap-2 p-3 bg-white border-t border-gray-200 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Type a message…"
          data-testid="chat-input"
          className="flex-1 h-12 rounded-full bg-gray-100 px-5 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
        />
        <button
          onClick={send}
          disabled={!text.trim() || sending}
          data-testid="chat-send"
          className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center disabled:opacity-40 active:scale-90 transition-all"
          aria-label="Send"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default RideChat;
